<script setup>
import { ref, watch, nextTick } from 'vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  messages: {
    type: Array,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  contactName: {
    type: String,
    default: '模拟客户',
  },
});

const messageContainer = ref(null);
const { formatMessage } = useMessageFormatter();

const isCustomerMessage = sender => sender === 'user';

const formatTime = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const scrollToBottom = async () => {
  await nextTick();
  if (messageContainer.value) {
    messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
  }
};

watch(() => props.messages.length, scrollToBottom);
watch(() => props.isLoading, scrollToBottom);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div
    ref="messageContainer"
    class="min-h-0 flex-1 overflow-y-auto bg-n-surface-1 px-3 py-4"
  >
    <div class="mx-auto flex min-h-full max-w-3xl flex-col justify-end gap-3">
      <div
        class="mx-auto rounded-full bg-n-alpha-2 px-3 py-1 text-center text-[11px] text-n-slate-9"
      >
        模拟会话 · 不会写入正式会话或发送邮件
      </div>

      <div
        v-if="!messages.length && !isLoading"
        class="flex flex-1 flex-col items-center justify-center pb-10 text-center"
      >
        <span
          class="i-lucide-message-square-dashed size-9 text-n-slate-8"
          aria-hidden="true"
        />
        <p class="mt-3 text-sm font-medium text-n-slate-12">空白对话</p>
        <p class="mt-1 text-xs text-n-slate-9">
          没有预置消息，请输入第一条模拟客户消息
        </p>
      </div>

      <div
        v-for="(message, index) in messages"
        :key="`${message.timestamp || index}-${index}`"
        class="flex"
        :class="
          isCustomerMessage(message.sender) ? 'justify-start' : 'justify-end'
        "
      >
        <div
          class="flex max-w-[95%] items-end gap-2 xl:max-w-[82%]"
          :class="
            isCustomerMessage(message.sender) ? 'flex-row' : 'flex-row-reverse'
          "
        >
          <Avatar
            v-if="!isCustomerMessage(message.sender)"
            :name="isCustomerMessage(message.sender) ? contactName : 'Captain'"
            rounded-full
            :size="24"
            class="shrink-0"
          />
          <div class="min-w-0">
            <div
              class="rounded-xl px-4 py-3 text-sm leading-6 [overflow-wrap:anywhere]"
              :class="[
                isCustomerMessage(message.sender)
                  ? 'rounded-bl-sm bg-n-alpha-2 text-n-slate-12'
                  : 'rounded-br-sm bg-n-amber-3 text-n-slate-12',
                message.isError ? 'outline outline-1 outline-n-ruby-7' : '',
              ]"
            >
              <div v-html="formatMessage(message.content)" />
            </div>
            <div
              class="mt-1 flex items-center gap-1 text-[11px] text-n-slate-9"
              :class="
                isCustomerMessage(message.sender)
                  ? 'justify-start'
                  : 'justify-end'
              "
            >
              <span v-if="message.agentName">{{ message.agentName }} ·</span>
              <span>{{ formatTime(message.timestamp) }}</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="flex justify-end">
        <div class="flex flex-row-reverse items-end gap-2">
          <Avatar name="Captain" rounded-full :size="24" />
          <div
            class="flex gap-1 rounded-xl rounded-br-sm bg-n-amber-3 px-4 py-4"
          >
            <div class="size-1.5 animate-bounce rounded-full bg-n-amber-10" />
            <div
              class="size-1.5 animate-bounce rounded-full bg-n-amber-10 [animation-delay:0.2s]"
            />
            <div
              class="size-1.5 animate-bounce rounded-full bg-n-amber-10 [animation-delay:0.4s]"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
